import { openDocumentOnClick } from '../helper/documentHelper';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { InputRef, TableColumnType } from 'antd';
import {
  Button,
  Flex,
  Grid,
  Input,
  message,
  Space,
  Table,
  Tag,
  theme,
  Tooltip,
  Typography,
} from 'antd';
import type { InternshipProcessWithNames } from '../Types/interfaces';
import { StepName } from '../Types/interfaces';
import {
  ExportCollaborationPackage,
  ExportPackageByProcessId,
  GetEvaluationReportById,
  GetInternshipProcessesOfUniversitySupervisor,
} from '../helper/apiHelper';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import { DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import { useTranslation } from 'react-i18next';
import type { JSX } from 'react';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { useCurrentStudyYear } from '../hooks/useStudyYear';
import { StudyYearDropdown } from './StudyYearDropdown';

const { useBreakpoint } = Grid;

// a document is either downloadable (icon button) or not there yet (muted dash)
const DocumentCell = ({
  url,
  label,
}: {
  url?: string | null;
  label: string;
}): JSX.Element =>
  url ? (
    <Tooltip title={label}>
      <Button
        shape="circle"
        icon={<DownloadOutlined />}
        href={url}
        target="_blank"
        onClick={openDocumentOnClick(url)}
        aria-label={label}
      />
    </Tooltip>
  ) : (
    <Typography.Text type="secondary">–</Typography.Text>
  );

interface DataType {
  key: React.Key;
  name: string;
  workSafety: string;
  // only students in a university internship have a work safety document
  hasWorkSafety: boolean;
  convetion: string;
  workreport: string;
  evaluationreport: string;
  researchagreement: string;
  allDocuments: number;
}

interface DocumentsListProps {
  containsCollaborationFiles: boolean;
  // shows a study year dropdown and only the documents of the selected study year
  filterByStudyYear?: boolean;
}

const DocumentsList = ({
  containsCollaborationFiles,
  filterByStudyYear = false,
}: DocumentsListProps): JSX.Element => {
  const { t } = useTranslation();
  const loggedInUser = useCurrentUser();
  const [processes, setProcesses] = useState<InternshipProcessWithNames[]>([]);
  const [data, setData] = useState<DataType[]>([]);
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  // undefined until the user selects a study year, then the dropdown shows the current one
  const [selectedStudyYearId, setSelectedStudyYearId] = useState<number>();
  const { data: currentStudyYear } = useCurrentStudyYear();
  const searchInput = useRef<InputRef>(null);
  type DataIndex = keyof DataType;
  const screens = useBreakpoint();
  const { token } = theme.useToken();

  const { Column } = Table;

  const getEvaluationReportUrl = useCallback(async (reportId: number) => {
    const response = await GetEvaluationReportById(reportId);
    return response.evaluationDocumentUrl;
  }, []);

  const getAllDocumentsUrl = useCallback(async (processIds: number[]) => {
    const response = await ExportPackageByProcessId(processIds);
    return response;
  }, []);

  const getAllCollaborationsUrl = useCallback(async () => {
    const response = await ExportCollaborationPackage();
    return response;
  }, []);

  const handleDownloadAll = async (processIds: number[]): Promise<void> => {
    const response = await getAllDocumentsUrl(processIds);
    const blob = new Blob([response], { type: 'application/zip' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `documents.zip`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadCollaborations = async (): Promise<void> => {
    const response = await getAllCollaborationsUrl();
    const blob = new Blob([response], { type: 'application/zip' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `collaborations.zip`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  function handleSearch(
    selectedKeys: string[],
    confirm: FilterDropdownProps['confirm'],
    dataIndex: DataIndex
  ): void {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  }

  const handleReset = (clearFilters: () => void): void => {
    clearFilters();
    setSearchText('');
  };

  // Extracted handlers to avoid deep nesting
  const handleInputKeyDown = (
    e: React.KeyboardEvent<HTMLButtonElement>
  ): void => {
    e.stopPropagation();
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setSelectedKeys: (selectedKeys: React.Key[]) => void
  ): void => {
    setSelectedKeys(e.target.value ? [e.target.value] : []);
  };

  const handleInputPressEnter = (
    selectedKeys: React.Key[],
    confirm: FilterDropdownProps['confirm'],
    dataIndex: DataIndex
  ): void => {
    handleSearch(selectedKeys as string[], confirm, dataIndex);
  };

  const handleSearchClick = (
    selectedKeys: React.Key[],
    confirm: FilterDropdownProps['confirm'],
    dataIndex: DataIndex
  ): void => {
    handleSearch(selectedKeys as string[], confirm, dataIndex);
  };

  const handleResetClick = (clearFilters: () => void): void => {
    if (clearFilters) handleReset(clearFilters);
  };

  const handleFilterClick = (
    confirm: FilterDropdownProps['confirm'],
    selectedKeys: React.Key[],
    dataIndex: DataIndex
  ): void => {
    confirm({ closeDropdown: false });
    setSearchText((selectedKeys as string[])[0]);
    setSearchedColumn(dataIndex);
  };

  const handleCloseClick = (close: () => void): void => {
    close();
  };

  const getColumnSearchProps = (
    dataIndex: DataIndex
  ): TableColumnType<DataType> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <button
        type="button"
        onKeyDown={handleInputKeyDown}
        style={{ border: 'none' }}
      >
        <div style={{ padding: 8 }}>
          <Input
            ref={searchInput}
            placeholder={t('convSignComp.searchStudent')}
            value={selectedKeys[0]}
            onChange={(e) => handleInputChange(e, setSelectedKeys)}
            onPressEnter={() =>
              handleInputPressEnter(selectedKeys, confirm, dataIndex)
            }
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() =>
                handleSearchClick(selectedKeys, confirm, dataIndex)
              }
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              {t('convSignComp.search')}
            </Button>
            <Button
              onClick={() => handleResetClick(clearFilters ?? ((): void => {}))}
              size="small"
              style={{ width: 90 }}
            >
              {t('convSignComp.reset')}
            </Button>
            <Button
              type="link"
              size="small"
              onClick={() =>
                handleFilterClick(confirm, selectedKeys, dataIndex)
              }
            >
              {t('convSignComp.filter')}
            </Button>
            <Button
              type="link"
              size="small"
              onClick={() => handleCloseClick(close)}
            >
              {t('convSignComp.close')}
            </Button>
          </Space>
        </div>
      </button>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toString().toLowerCase())
        : false,
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: '#ffc069',
            padding: 0,
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  });

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys: React.Key[]): void => {
      setSelectedRowKeys(selectedKeys);
      console.log('Selected Row Keys:', selectedKeys);
    },
  };

  useEffect(() => {
    const getStudentsOfSupervisor = async (): Promise<void> => {
      const processesResp = await GetInternshipProcessesOfUniversitySupervisor(
        loggedInUser.id
      );
      setProcesses(processesResp);
    };
    getStudentsOfSupervisor();
  }, [loggedInUser.id]);

  // the dropdown shows the current study year until the user selects another one, the same is filtered here
  const shownStudyYearId = selectedStudyYearId ?? currentStudyYear?.id;

  // memoized, because a new array on every render would run the effect below again and again
  const shownProcesses = useMemo(
    () =>
      filterByStudyYear
        ? processes.filter(
            (process) => process.studyYearId === shownStudyYearId
          )
        : processes,
    [processes, filterByStudyYear, shownStudyYearId]
  );

  const handleStudyYearChange = (studyYearId: number): void => {
    setSelectedStudyYearId(studyYearId);
    // the selected rows of the previous study year would be downloaded too, while they aren't shown
    setSelectedRowKeys([]);
  };

  useEffect(() => {
    if (!shownProcesses) {
      return;
    }
    const fetchEvaluationReportUrls = async (): Promise<void> => {
      // a study year without processes has to empty the table, not keep the rows of the previous one
      const newData = await Promise.all(
        shownProcesses.map((process) => mapProcessToData(process))
      );
      setData(newData);
    };

    const mapProcessToData = async (
      process: InternshipProcessWithNames
    ): Promise<DataType> => {
      const evaluationReportUrl = process.evaluationReportId
        ? await getEvaluationReportUrl(process.evaluationReportId).catch(
            () => ''
          )
        : '';

      return {
        key: process.id,
        name: process.studentName,
        workSafety: process.workSafetyDocumentUrl ?? '',
        hasWorkSafety:
          !!process.workSafetyDocumentUrl ||
          !!process.processSteps?.some(
            (step) => step.stepName === StepName.workSafetyInstructionReading
          ),
        convetion: process.conventionDocumentUrl,
        workreport: process.workLogDocumentUrl,
        evaluationreport: evaluationReportUrl,
        researchagreement: process.researchAgreementDocumentUrl,
        allDocuments: process.id,
      };
    };

    fetchEvaluationReportUrls();
  }, [shownProcesses, getEvaluationReportUrl]);

  const downloadColumn = (
    title: string,
    dataIndex: keyof DataType,
    downloadLabel: string
  ): JSX.Element => (
    <Column
      title={title}
      dataIndex={dataIndex}
      key={dataIndex}
      align="center"
      render={(url: string) => <DocumentCell url={url} label={downloadLabel} />}
    />
  );

  return (
    <>
      <Flex
        justify="space-between"
        align="center"
        wrap
        gap={12}
        style={{ marginBottom: 16 }}
      >
        <Flex align="center" gap={12} wrap>
          {filterByStudyYear && (
            <Flex align="center" gap={8}>
              <Typography.Text strong>{t('studyYear.label')}:</Typography.Text>
              <StudyYearDropdown
                value={selectedStudyYearId}
                onChange={handleStudyYearChange}
              />
            </Flex>
          )}
          <Tag
            bordered={false}
            color={selectedRowKeys.length > 0 ? 'processing' : 'default'}
            style={{ margin: 0, padding: '4px 10px', fontSize: 13 }}
          >
            {t('listDocumentsComp.selected')} {selectedRowKeys.length}
          </Tag>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={() => {
              if (selectedRowKeys.length === 0) {
                message.warning(t('listDocumentsComp.noRowsSelected'));
                return;
              }
              handleDownloadAll(selectedRowKeys as number[]);
            }}
          >
            {t('listDocumentsComp.downloadSelected')}
          </Button>
        </Flex>
        {containsCollaborationFiles && (
          <Button
            icon={<DownloadOutlined />}
            onClick={() => handleDownloadCollaborations()}
          >
            {t('listDocumentsComp.downloadCollaborations')}
          </Button>
        )}
      </Flex>

      <Table<DataType>
        dataSource={data}
        rowSelection={rowSelection} // Add row selection
        scroll={{ x: 'max-content' }}
        size={screens.lg ? 'middle' : 'small'}
        style={{
          border: `1px solid ${token.colorBorderSecondary}`,
          borderRadius: token.borderRadiusLG,
          overflow: 'hidden',
        }}
      >
        <Column
          title={t('listDocumentsComp.name')}
          dataIndex="name"
          key="name"
          {...getColumnSearchProps('name')}
        />
        {data.some((row) => row.hasWorkSafety) && (
          <Column
            title={t('listDocumentsComp.workSafety')}
            dataIndex="workSafety"
            key="workSafety"
            align="center"
            render={(url: string, record: DataType) =>
              record.hasWorkSafety ? (
                <DocumentCell
                  url={url}
                  label={t('listDocumentsComp.workSafetyDownload')}
                />
              ) : null
            }
          />
        )}
        {downloadColumn(
          t('listDocumentsComp.conv'),
          'convetion',
          t('listDocumentsComp.convDownload')
        )}
        {downloadColumn(
          t('listDocumentsComp.workReport'),
          'workreport',
          t('listDocumentsComp.workReportDownload')
        )}
        {downloadColumn(
          t('listDocumentsComp.evalReport'),
          'evaluationreport',
          t('listDocumentsComp.evalReportDownload')
        )}
        {downloadColumn(
          t('listDocumentsComp.researchAgreement'),
          'researchagreement',
          t('listDocumentsComp.researchAgreementDownload')
        )}
        <Column
          title={t('listDocumentsComp.allDocs')}
          dataIndex="allDocuments"
          key="allDocuments"
          align="center"
          render={(processId: number) => (
            <Button
              type="primary"
              size="small"
              ghost
              icon={<DownloadOutlined />}
              onClick={() => handleDownloadAll([processId])}
            >
              {t('listDocumentsComp.downloadAll')}
            </Button>
          )}
        />
      </Table>
    </>
  );
};

export default DocumentsList;
